<template>
    <v-form ref="form" @submit.prevent="save">
        <v-card class="mb-5 pa-2">
            <card-header
                v-if="!newReseller"
                :title="
                    $t('forms.title', {
                        prefix: titlePrefix,
                        resource: $tc('resources.address.name', 1),
                    })
                "
            />
            <v-card-text>
                <v-row>
                    <v-col cols="12" xs="12" sm="12" md="3">
                        <v-text-field
                            v-model="address.zip_code"
                            :label="$t('resources.address.attributes.zipCode')"
                            :counter="9"
                            v-mask="'#####-###'"
                            :error-messages="zipCodeErrors"
                            @input="$v.address.zip_code.$touch()"
                            @blur="findZipCode()"
                            :autofocus="!newReseller"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="3">
                        <v-select
                            v-model="selectedState"
                            :items="states"
                            item-text="name"
                            :label="$t('resources.address.attributes.state')"
                            @change="loadCities()"
                            return-object
                        ></v-select>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="6">
                        <v-select
                            :disabled="cities.length === 0"
                            :loading="
                                selectedState !== null && cities.length === 0
                            "
                            v-model="selectedCity"
                            :items="cities"
                            item-text="name"
                            item-value="id"
                            :label="$t('resources.address.attributes.city')"
                            return-object
                        ></v-select>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="5">
                        <v-text-field
                            v-model="address.district"
                            :label="$t('resources.address.attributes.district')"
                            :counter="80"
                            :error-messages="districtErrors"
                            @input="$v.address.district.$touch()"
                            @blur="$v.address.district.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="7">
                        <v-text-field
                            v-model="address.street"
                            :label="$t('resources.address.attributes.street')"
                            :counter="100"
                            :error-messages="streetErrors"
                            @input="$v.address.street.$touch()"
                            @blur="$v.address.street.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="5">
                        <v-text-field
                            v-model="address.number"
                            :label="$t('resources.address.attributes.number')"
                            :counter="10"
                            :error-messages="numberErrors"
                            @input="$v.address.number.$touch()"
                            @blur="$v.address.number.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="7">
                        <v-text-field
                            v-model="address.complement"
                            :label="
                                $t('resources.address.attributes.complement')
                            "
                            :counter="255"
                            :error-messages="complementErrors"
                            @input="$v.address.complement.$touch()"
                            @blur="$v.address.complement.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12" xs="12" sm="12" md="12">
                        <v-switch
                            v-if="!newReseller"
                            :color="
                                address.fiscal_address ? 'success' : 'error'
                            "
                            inset
                            v-model="address.fiscal_address"
                            :label="
                                $t('resources.address.attributes.fiscalAddress')
                            "
                            false-value
                        ></v-switch>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions v-if="!newReseller" :loadingSave="loading" />
        </v-card>
    </v-form>
</template>

<script>
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import { validationMixin } from 'vuelidate'
import { maxLength, required } from 'vuelidate/lib/validators'
import BaseModel from '@/models/BaseModel'
import State from '@/models/State'
import City from '@/models/City'
import { mask } from 'vue-the-mask'

export default {
    components: {
        CardHeader,
        FormActions,
    },

    directives: { mask },

    mixins: [validationMixin],

    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        newReseller: {
            type: Boolean,
            default: () => {
                return false
            },
        },
        titlePrefix: String,
    },

    data() {
        return {
            loading: false,
            states: [],
            selectedState: null,
            cities: [],
            selectedCity: null,
            address: this.model.clone(),
        }
    },

    validations() {
        return {
            address: {
                zip_code: { required, maxLength: maxLength(9) },
                street: {
                    required,
                    maxLength: maxLength(100),
                },
                number: { maxLength: maxLength(10) },
                district: {
                    required,
                    maxLength: maxLength(80),
                },
                complement: { maxLength: maxLength(255) },
                city_id: {
                    required,
                },
            },
        }
    },

    methods: {
        validate() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                return false
            }

            return true
        },

        async save() {
            this.loading = true

            if (!this.validate()) {
                this.loading = false
                return
            }

            let clone = this.address.clone()
            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$emit('saved')

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('forms.messages.saved', {
                    resource: this.$tc(
                        'resources.address.name',
                        1,
                    ).toLowerCase(),
                    name: clone.addressShort,
                }),
            )
        },

        clear() {
            this.$refs.form.reset()
        },

        async loadStates() {
            let res = await this.stateModel.all()

            if (!res) return

            this.states = res.data
        },

        async loadCities() {
            this.cities = []
            this.selectedCity = null

            if (!this.selectedState) return

            let res = await this.cityModel
                .where('state_id', this.selectedState.id)
                .get()

            if (!res) return

            this.cities = res.data
        },

        async findZipCode() {
            if (
                typeof this.address.zip_code == 'undefined' ||
                this.address.zip_code.length != 9
            )
                return

            let data = await this.address.findZipCode()
            let address = this.address.clone()

            if (data === null) {
                address.street = null
                address.district = null
                this.address = address
                this.selectedState = null
                this.selectedCity = null
                return
            }

            address.street = data.street
            address.district = data.neighborhood
            this.address = address
            this.selectedState = this.states.find(state => {
                return state.id == data.relations.city.state_id
            })

            await this.loadCities()

            this.selectedCity = this.cities.find(city => {
                return city.id == data.relations.city.id
            })

            this.$v.$touch()
        },
    },

    computed: {
        stateModel() {
            return new State()
        },

        cityModel() {
            return new City()
        },

        zipCodeErrors() {
            const errors = []
            if (!this.$v.address.zip_code.$dirty) return errors

            !this.$v.address.zip_code.required &&
                errors.push(
                    this.$t('resources.address.errors.zip-code-required'),
                )
            !this.$v.address.zip_code.maxLength &&
                errors.push(
                    this.$t('resources.address.errors.zip-code-max-length'),
                )

            return errors
        },

        streetErrors() {
            const errors = []
            if (!this.$v.address.street.$dirty) return errors

            !this.$v.address.street.required &&
                errors.push(
                    this.$t('resources.address.errors.street-required-if'),
                )
            !this.$v.address.street.maxLength &&
                errors.push(
                    this.$t('resources.address.errors.street-max-length'),
                )

            return errors
        },

        numberErrors() {
            const errors = []
            if (!this.$v.address.number.$dirty) return errors

            !this.$v.address.number.maxLength &&
                errors.push(
                    this.$t('resources.address.errors.number-max-length'),
                )

            return errors
        },

        districtErrors() {
            const errors = []
            if (!this.$v.address.district.$dirty) return errors

            !this.$v.address.district.required &&
                errors.push(
                    this.$t('resources.address.errors.district-required-if'),
                )
            !this.$v.address.district.maxLength &&
                errors.push(
                    this.$t('resources.address.errors.district-max-length'),
                )

            return errors
        },

        complementErrors() {
            const errors = []
            if (!this.$v.address.complement.$dirty) return errors

            !this.$v.address.complement.maxLength &&
                errors.push(
                    this.$t('resources.address.errors.complement-max-length'),
                )

            return errors
        },

        cityErrors() {
            const errors = []
            if (!this.$v.address.city.$dirty) return errors

            !this.$v.address.city.required &&
                errors.push(
                    this.$t('resources.address.errors.city-required-if'),
                )

            return errors
        },
    },

    watch: {
        selectedCity: function(newValue) {
            let address = this.address
            if (newValue === null) {
                address.city_id = null
                this.address = address
                return
            }

            address.city_id = newValue.id
            this.address = address
        },
    },

    created() {
        this.loadStates().then(() => {
            if (this.address.state_id) {
                this.selectedState = this.states.find(state => {
                    return state.id == this.address.state_id
                })
            }

            this.loadCities().then(() => {
                if (this.address.city_id) {
                    this.selectedCity = this.cities.find(city => {
                        return city.id == this.address.city_id
                    })
                }
            })
        })
    },
}
</script>

<style></style>
